import { inject, Injectable } from "@angular/core";
import { IPlannedEvent } from "@ramudden/models/planned-event";
import { LocalStorageService } from "@ramudden/services";
import { ISelectedWorker } from "../components/worker/worker.component";
import { ISelectedVehicle } from "../pages/setup/dialogs/vehicle/vehicle-dialog.component";
import { GlobalEventsService } from "./generic/global-events.service";

export type AppConfig = {
    language: string;
    isConfigured: boolean;
    plannedEventId: number;
};

const configKey = "configKey";
const coordinatorModeKey = "coordinatorModeKey";
const excludedWorkersKey = "excludedWorkersKey";
const isCoordinatorKey = "isCoordinatorKey";
const selectedPlannedEventKey = "selectedPlannedEventKey";
const selectedVehiclesKey = "selectedVehiclesKey";
const selectedWorkerKey = "selectedWorkerKey";
const selectedWorkersKey = "selectedWorkersKey";
const viewingPlannedEventKey = "viewingPlannedEventKey";
const inCoordinatorViewKey = "inCoordinatorViewKey";

@Injectable({
    providedIn: "root",
})
export class AppService {
    private readonly localStorageService = inject(LocalStorageService);
    private readonly globalEventsService = inject(GlobalEventsService);

    config: AppConfig = undefined;
    coordinatorMode = false;
    excludedWorkers: ISelectedWorker[] = [];
    inCoordinatorView = false;
    isCoordinator = false;
    plannedEvent: IPlannedEvent = undefined;
    selectedVehicles: ISelectedVehicle[] = [];
    selectedWorker: ISelectedWorker = undefined;
    selectedWorkers: ISelectedWorker[] = [];
    viewingPlannedEvent: IPlannedEvent = undefined;

    /** Transient flag: true only during coordinator Control task creation. Not persisted. */
    isCoordinatorControlTask = false;

    /** Whether the current session is a readonly coordinator view (spectating or viewing another team). */
    get isReadonlyView(): boolean {
        if (this.isCoordinatorControlTask) return false;
        return this.coordinatorMode || this.inCoordinatorView || (this.isCoordinator && !!this.viewingPlannedEvent);
    }

    constructor() {
        this.restoreState();

        // The logged-in account is usually still unknown during the first restore, so re-apply the exclusion as soon
        // as the authorization info arrives. Devices configured before SMAAS-70 still carry a true coordinator flag.
        this.globalEventsService.authorizationInfo$.subscribe(() => this.clearCoordinatorStateForExcludedAccount());
    }

    public saveState() {
        this.localStorageService.setItem(configKey, JSON.stringify(this.config));
        this.localStorageService.setItem(coordinatorModeKey, JSON.stringify(this.coordinatorMode));
        this.localStorageService.setItem(excludedWorkersKey, JSON.stringify(this.excludedWorkers));
        this.localStorageService.setItem(inCoordinatorViewKey, JSON.stringify(this.inCoordinatorView));
        this.localStorageService.setItem(isCoordinatorKey, JSON.stringify(this.isCoordinator));
        this.localStorageService.setItem(selectedPlannedEventKey, JSON.stringify(this.plannedEvent));
        this.localStorageService.setItem(selectedVehiclesKey, JSON.stringify(this.selectedVehicles));
        this.localStorageService.setItem(selectedWorkerKey, JSON.stringify(this.selectedWorker));
        this.localStorageService.setItem(selectedWorkersKey, JSON.stringify(this.selectedWorkers));
        this.localStorageService.setItem(viewingPlannedEventKey, JSON.stringify(this.viewingPlannedEvent));
    }

    public restoreState() {
        const parse = (key: string) => {
            const value = this.localStorageService.getItem(key);
            if (value === null || value === undefined || value === "undefined") return null;
            return JSON.parse(value);
        };

        this.config = parse(configKey);
        this.coordinatorMode = parse(coordinatorModeKey) ?? false;
        this.excludedWorkers = parse(excludedWorkersKey);
        this.inCoordinatorView = parse(inCoordinatorViewKey) ?? false;
        this.isCoordinator = parse(isCoordinatorKey) ?? false;
        this.plannedEvent = parse(selectedPlannedEventKey);
        this.selectedVehicles = parse(selectedVehiclesKey);
        this.selectedWorker = parse(selectedWorkerKey);
        this.selectedWorkers = parse(selectedWorkersKey);
        this.viewingPlannedEvent = parse(viewingPlannedEventKey);

        // Fix conflicting state: can't be both spectating and in coordinator view
        if (this.coordinatorMode && this.inCoordinatorView) {
            this.inCoordinatorView = false;
        }

        this.clearCoordinatorStateForExcludedAccount();
    }

    /**
     * Drops any coordinator state when the logged-in account is a shared login that must never get the coordinator
     * (werfleiding) user interface. Only the coordinator keys are rewritten, so setup state in progress is untouched.
     * Does nothing while the account is still unknown; the constructor re-applies it once the account arrives.
     */
    private clearCoordinatorStateForExcludedAccount() {
        if (!this.globalEventsService.isNonCoordinatorAccount()) return;
        if (!this.coordinatorMode && !this.inCoordinatorView && !this.isCoordinator && !this.viewingPlannedEvent) {
            return;
        }

        this.coordinatorMode = false;
        this.inCoordinatorView = false;
        this.isCoordinator = false;
        this.viewingPlannedEvent = undefined;

        this.localStorageService.setItem(coordinatorModeKey, JSON.stringify(false));
        this.localStorageService.setItem(inCoordinatorViewKey, JSON.stringify(false));
        this.localStorageService.setItem(isCoordinatorKey, JSON.stringify(false));
        this.localStorageService.removeItem(viewingPlannedEventKey);
    }

    public clearState() {
        this.config = undefined;
        this.localStorageService.removeItem(configKey);
        this.coordinatorMode = false;
        this.localStorageService.removeItem(coordinatorModeKey);
        this.excludedWorkers = [];
        this.localStorageService.removeItem(excludedWorkersKey);
        this.inCoordinatorView = false;
        this.localStorageService.removeItem(inCoordinatorViewKey);
        this.isCoordinator = false;
        this.localStorageService.removeItem(isCoordinatorKey);
        this.plannedEvent = undefined;
        this.localStorageService.removeItem(selectedPlannedEventKey);
        this.selectedWorker = undefined;
        this.localStorageService.removeItem(selectedWorkerKey);
        this.selectedWorkers = [];
        this.localStorageService.removeItem(selectedWorkersKey);
        this.selectedVehicles = [];
        this.localStorageService.removeItem(selectedVehiclesKey);
        this.viewingPlannedEvent = undefined;
        this.localStorageService.removeItem(viewingPlannedEventKey);
        this.isCoordinatorControlTask = false;
    }
}
